// ========================================
// components/InheritanceSection.js - Inherited Assets Display
// ========================================
// FILE PATH: frontend/src/components/InheritanceSection.js
// PURPOSE: Displays read-only inherited assets for beneficiaries
// These assets come from deceased users and cannot be edited or deleted
// They serve as permanent legal proof of inheritance
// ========================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const InheritanceSection = () => {
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [inheritance, setInheritance] = useState([]);      // List of inherited assets
  const [loading, setLoading] = useState(true);            // Loading state
  const [error, setError] = useState('');                  // Error message
  const [selectedAsset, setSelectedAsset] = useState(null); // Currently selected asset for modal
  const [showDetails, setShowDetails] = useState(false);    // Modal visibility
  const [downloading, setDownloading] = useState(false);    // PDF download in progress
  const [downloadingDoc, setDownloadingDoc] = useState(false); // Document download in progress

  // ========================================
  // FETCH INHERITED ASSETS ON COMPONENT MOUNT
  // ========================================
  useEffect(() => {
    fetchInheritance();
  }, []);

  // ========================================
  // FUNCTION: Fetch inheritance records from API
  // ========================================
  const fetchInheritance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/inheritance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInheritance(response.data);
    } catch (err) {
      setError('Failed to load inheritance records');
      console.error('Error fetching inheritance:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FUNCTION: View full asset details in modal
  // ========================================
  const viewDetails = async (inheritanceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/inheritance/${inheritanceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedAsset(response.data);
      setShowDetails(true);
    } catch (err) {
      alert('Failed to load asset details');
    }
  };

  // ========================================
  // FUNCTION: Download PDF inheritance certificate
  // ========================================
  const downloadPDF = async (inheritanceId, certificateNumber) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/inheritance/${inheritanceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inheritance-${certificateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF generation coming soon');
    } finally {
      setDownloading(false);
    }
  };

  // ========================================
  // FUNCTION: Download supporting document for inherited asset
  // Only available if original asset had a document uploaded
  // ========================================
  const downloadDocument = async (inheritanceId, assetSnapshot) => {
    if (!assetSnapshot?.document?.fileUrl) {
      alert('No supporting document available for this asset');
      return;
    }
    
    setDownloadingDoc(true);
    try {
      const token = localStorage.getItem('token');
      // Note: This endpoint may need to be added to inheritance routes
      // For now, we attempt to download from the original asset endpoint
     // const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${assetSnapshot.originalAssetId}/document`, {
      //  headers: { 'Authorization': `Bearer ${token}` },
      //  responseType: 'blob'
     // });
	  
	  // Use the inheritance document endpoint
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/inheritance/${inheritanceId}/document`, {
      headers: { 'Authorization': `Bearer ${token}` },
      responseType: 'blob'
    });
      
      const blob = new Blob([response.data], { type: assetSnapshot.document.fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', assetSnapshot.document.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      alert('Failed to download document. The original document may no longer be available.');
      console.error('Document download error:', err);
    } finally {
      setDownloadingDoc(false);
    }
  };

  // ========================================
  // HELPER: Get asset icon based on type
  // ========================================
  const getAssetIcon = (type) => {
    const icons = {
      bank_account: '🏦',
      real_estate: '🏠',
      vehicle: '🚗',
      investment: '📈',
      cryptocurrency: '₿',
      will: '📜',
      document: '📄',
      digital_asset: '🔑',
      other: '📦'
    };
    return icons[type] || '📦';
  };

  // ========================================
  // HELPER: Format date for display
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ========================================
  // RENDER LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading inheritance records...</p>
      </div>
    );
  }

  // ========================================
  // RENDER ERROR STATE
  // ========================================
  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>{error}</p>
      </div>
    );
  }

  // ========================================
  // RENDER EMPTY STATE
  // ========================================
  if (inheritance.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyIcon}>📭</div>
        <h3 style={styles.emptyTitle}>No Inherited Assets</h3>
        <p style={styles.emptyText}>
          You haven't received any inheritance yet. When someone names you as a beneficiary,
          their assets will appear here after death verification.
        </p>
      </div>
    );
  }

  // ========================================
  // RENDER MAIN VIEW
  // ========================================
  return (
    <div style={styles.container}>
      {/* ======================================== */}
      {/* HEADER SECTION */}
      {/* ======================================== */}
      <div style={styles.header}>
        <h2 style={styles.title}>📜 Inherited Assets</h2>
        <p style={styles.subtitle}>
          These assets have been left to you. They are permanent legal records
          and cannot be edited or deleted.
        </p>
      </div>

      {/* ======================================== */}
      {/* INHERITANCE LIST */}
      {/* ======================================== */}
      <div style={styles.listContainer}>
        {inheritance.map((item) => (
          <div key={item.id} style={styles.card}>
            {/* Card Header */}
            <div style={styles.cardHeader}>
              <div style={styles.iconContainer}>
                <span style={styles.icon}>{getAssetIcon(item.assetType)}</span>
              </div>
              <div style={styles.headerInfo}>
                <h3 style={styles.assetName}>{item.assetName}</h3>
                <p style={styles.assetType}>{item.assetType?.replace('_', ' ')}</p>
              </div>
              <div style={styles.badge}>
                <span style={styles.badgeText}>INHERITED</span>
              </div>
            </div>

            {/* Certificate Number Badge (if available) */}
            {item.certificateNumber && (
              <div style={styles.certBadgeContainer}>
                <span style={styles.certBadge}>📜 Certificate: {item.certificateNumber}</span>
              </div>
            )}

            {/* Integrity Warning (if original asset was tampered) */}
            {item.assetSnapshot?.isTampered && (
              <div style={styles.integrityWarning}>
                <span>⚠️</span>
                <span>Warning: The original record of this asset shows signs of tampering.</span>
              </div>
            )}

            {/* Card Details */}
            <div style={styles.cardDetails}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>From:</span>
                <span style={styles.detailValue}>{item.deceasedName}</span>
              </div>
              {item.estimatedValue && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Value:</span>
                  <span style={styles.detailValue}>GHS {item.estimatedValue.toLocaleString()}</span>
                </div>
              )}
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Inherited:</span>
                <span style={styles.detailValue}>{formatDate(item.inheritedAt)}</span>
              </div>
            </div>

            {/* Card Actions */}
            <div style={styles.cardActions}>
              <button
                onClick={() => viewDetails(item.id)}
                style={styles.detailsButton}
              >
                View Details
              </button>
              <button
                onClick={() => downloadPDF(item.id, item.certificateNumber)}
                disabled={downloading}
                style={styles.downloadButton}
              >
                {downloading ? 'Downloading...' : '📄 Download Certificate'}
              </button>
              {item.assetSnapshot?.document?.fileUrl && (
                <button
                  onClick={() => downloadDocument(item.assetSnapshot)}
                  disabled={downloadingDoc}
                  style={styles.documentButton}
                >
                  {downloadingDoc ? 'Downloading...' : '📎 Document'}
                </button>
              )}
            </div>

            {/* Read-only Note */}
            <div style={styles.readonlyNote}>
              <span>🔒</span>
              <span>This is a permanent legal record and cannot be modified</span>
            </div>
          </div>
        ))}
      </div>

      {/* ======================================== */}
      {/* ASSET DETAILS MODAL */}
      {/* ======================================== */}
      {showDetails && selectedAsset && (
        <div style={styles.modalOverlay} onClick={() => setShowDetails(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setShowDetails(false)}>×</button>
            
            <h3 style={styles.modalTitle}>Inherited Asset Details</h3>
            
            <div style={styles.modalContent}>
              <div style={styles.modalSection}>
                <h4>Asset Information</h4>
                <p><strong>Name:</strong> {selectedAsset.assetName}</p>
                <p><strong>Type:</strong> {selectedAsset.assetType?.replace('_', ' ')}</p>
                {selectedAsset.certificateNumber && (
                  <p><strong>Certificate:</strong> <code>{selectedAsset.certificateNumber}</code></p>
                )}
              </div>

              <div style={styles.modalSection}>
                <h4>Original Owner</h4>
                <p><strong>Name:</strong> {selectedAsset.deceasedName}</p>
                <p><strong>Inherited:</strong> {formatDate(selectedAsset.inheritedAt)}</p>
              </div>

              {selectedAsset.assetSnapshot && (
                <div style={styles.modalSection}>
                  <h4>Asset Details</h4>
                  {selectedAsset.assetSnapshot.institutionName && (
                    <p><strong>Institution:</strong> {selectedAsset.assetSnapshot.institutionName}</p>
                  )}
                  {selectedAsset.assetSnapshot.accountNumber && (
                    <p><strong>Account:</strong> {selectedAsset.assetSnapshot.accountNumber}</p>
                  )}
                  {selectedAsset.assetSnapshot.balance && (
                    <p><strong>Balance:</strong> GHS {selectedAsset.assetSnapshot.balance.toLocaleString()}</p>
                  )}
                  {selectedAsset.assetSnapshot.address && (
                    <p><strong>Address:</strong> {selectedAsset.assetSnapshot.address}</p>
                  )}
                  {selectedAsset.assetSnapshot.titleDeedNumber && (
                    <p><strong>Title Deed:</strong> {selectedAsset.assetSnapshot.titleDeedNumber}</p>
                  )}
                  {selectedAsset.assetSnapshot.make && (
                    <p><strong>Vehicle:</strong> {selectedAsset.assetSnapshot.make} {selectedAsset.assetSnapshot.model} ({selectedAsset.assetSnapshot.year})</p>
                  )}
                  {selectedAsset.assetSnapshot.vin && (
                    <p><strong>VIN:</strong> {selectedAsset.assetSnapshot.vin}</p>
                  )}
                  {selectedAsset.assetSnapshot.description && (
                    <p><strong>Description:</strong> {selectedAsset.assetSnapshot.description}</p>
                  )}
                  
                  {/* Document info in modal */}
                  {selectedAsset.assetSnapshot.document?.fileName && (
                    <div style={styles.modalDocumentInfo}>
                      <p><strong>Supporting Document:</strong> {selectedAsset.assetSnapshot.document.fileName}</p>
                      <button
                        onClick={() => downloadDocument(selectedAsset.assetSnapshot)}
                        disabled={downloadingDoc}
                        style={styles.modalDocButton}
                      >
                        {downloadingDoc ? 'Downloading...' : 'Download Document'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => downloadPDF(selectedAsset.id, selectedAsset.certificateNumber)}
                style={styles.modalDownloadButton}
              >
                Download Certificate
              </button>
              <button
                onClick={() => setShowDetails(false)}
                style={styles.modalCloseButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// STYLES
// ========================================
const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F5F0E6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '2px solid #FFD700',
  },
  title: {
    fontSize: '28px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
  },
  listContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
    transition: 'transform 0.2s',
    ':hover': {
      transform: 'translateY(-2px)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
  },
  iconContainer: {
    width: '50px',
    height: '50px',
    backgroundColor: '#F5F0E6',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: '24px',
  },
  headerInfo: {
    flex: 1,
  },
  assetName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    margin: 0,
  },
  assetType: {
    fontSize: '12px',
    color: '#666',
    margin: 0,
    textTransform: 'capitalize',
  },
  badge: {
    backgroundColor: '#2E7D32',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  badgeText: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: 'white',
  },
  certBadgeContainer: {
    marginBottom: '10px',
  },
  certBadge: {
    fontSize: '11px',
    backgroundColor: '#F5F0E6',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#2E7D32',
    fontFamily: 'monospace',
  },
  integrityWarning: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    borderRadius: '5px',
    padding: '8px',
    marginBottom: '10px',
    fontSize: '12px',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardDetails: {
    marginBottom: '15px',
    padding: '10px',
    backgroundColor: '#F9F9F9',
    borderRadius: '8px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '13px',
  },
  detailLabel: {
    color: '#666',
  },
  detailValue: {
    color: '#1A1A1A',
    fontWeight: '500',
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  },
  detailsButton: {
    flex: 1,
    padding: '8px',
    backgroundColor: 'transparent',
    border: '1px solid #2E7D32',
    color: '#2E7D32',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  downloadButton: {
    flex: 1,
    padding: '8px',
    backgroundColor: '#FFD700',
    border: 'none',
    color: '#1A1A1A',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  documentButton: {
    padding: '8px 12px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  readonlyNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
    fontSize: '11px',
    color: '#999',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    backgroundColor: '#F5F0E6',
    minHeight: '400px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F5F0E6',
    borderTop: '4px solid #FFD700',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px',
    color: '#dc2626',
  },
  errorText: {
    fontSize: '14px',
  },
  emptyContainer: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: 'white',
    borderRadius: '10px',
    margin: '20px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '20px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#666',
    maxWidth: '400px',
    margin: '0 auto',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '30px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'relative',
  },
  modalClose: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  modalTitle: {
    fontSize: '20px',
    color: '#FFD700',
    marginBottom: '20px',
  },
  modalContent: {
    marginBottom: '20px',
  },
  modalSection: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee',
  },
  modalDocumentInfo: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #eee',
  },
  modalDocButton: {
    marginTop: '5px',
    padding: '6px 12px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  modalFooter: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  modalDownloadButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  modalCloseButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    cursor: 'pointer',
  },
};

export default InheritanceSection;